import { useState, useMemo, useEffect } from "react";
import {
    Plus,
    Building2,
    Search,
    LayoutGrid,
    Loader2,
    AlertCircle,
    Users,
    GraduationCap,
    Pencil,
    EyeOff,
    SlidersHorizontal,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RowActions } from "@/components/RowActions";
import Cookies from "js-cookie";
import { environment } from "@/env/env.prod";
import { DepartmentRoster } from "./DepartmentRoster";
import { DepartmentEditDialog } from "./DepartmentEditDialog";
import { ProgrammesPanel } from "./ProgrammesPanel";
import { AdvancedSettings } from "./AdvancedSettings";
import { ProgrammesTable } from "./ProgrammesTable";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { type DepartmentProfile, listDepartments } from "./structureApi";
import { useInstituteLabels } from "@/lib/instituteLabels";

type Department = {
    name: string;            // shown name (profile display name)
    // The raw toFcmTopic-normalized value (underscored) — the actual
    // identifier Join_Department/Get_Department_Members expect. `name`
    // above is the display name, so this has to be kept separately rather
    // than derived back.
    rawName: string;
    code: string;
    head: string;
    hidden: boolean;
    creatorName?: string;
    creatorEmail?: string;
    profile: DepartmentProfile;
};

// Older departments have no typed name: the stored key ("computer_science")
// is shown with spaces, as this page always did.
const shownName = (p: DepartmentProfile) =>
    p.displayName === p.departmentName ? p.departmentName.replace(/_/g, " ") : p.displayName;

// initialView: open on the Programmes tab (the side menu's Programmes entry).
export default function DepartmentsPage({ initialView = "departments" }: { initialView?: "departments" | "programmes" } = {}) {
    const L = useInstituteLabels();
    const [ departments, setDepartments ] = useState<Department[]>([]);
    const [ search, setSearch ] = useState("");

    // Loading / error states
    const [ isLoading, setIsLoading ] = useState(false);
    const [ listError, setListError ] = useState<string | null>(null);

    // Roster dialog
    const [ rosterDept, setRosterDept ] = useState<Department | null>(null);
    // Edit profile / programmes
    const [ editDept, setEditDept ] = useState<DepartmentProfile | null>(null);
    const [ programmesDept, setProgrammesDept ] = useState<DepartmentProfile | null>(null);
    // Advanced: attendance policy (admin / superadmin)
    const [ policyDept, setPolicyDept ] = useState<DepartmentProfile | null>(null);
    // Which table: departments, or every programme (paged)
    const [ view, setView ] = useState<"departments" | "programmes">(initialView);
    // bumped when the programmes window closes, so the Programmes tab refetches
    const [ programmesVersion, setProgrammesVersion ] = useState(0);

    // Create dialog
    const [ createOpen, setCreateOpen ] = useState(false);
    const [ depName, setDepName ] = useState("");
    const [ isCreating, setIsCreating ] = useState(false);
    const [ createError, setCreateError ] = useState<string | null>(null);

    // Role check — only superadmin and admin can create / edit departments;
    // programmes also subadmin (same as the server's CASL rules)
    const currentRole = Cookies.get("role");
    const canCreate = currentRole === "superadmin" || currentRole === "admin";
    const canManageProgrammes = canCreate || currentRole === "subadmin";

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        if (!q) return departments;
        return departments.filter(
            (d) =>
                d.name.toLowerCase().includes(q) ||
                d.code.toLowerCase().includes(q) ||
                d.head.toLowerCase().includes(q)
        );
    }, [ search, departments ]);

    useEffect(() => {
        fetchDepartments();
    }, []);

    // Creator details still come from Get_Department_list; the profiles
    // (name, head, hidden) from classrooms/listDepartments. If the profiles
    // route isn't there (server not updated yet) the page works as before.
    const fetchCreators = async (): Promise<any[] | null> => {
        const authToken = Cookies.get("authToken");
        const parent = Cookies.get("ins_code");
        const res = await fetch(`${environment.BASE_URL}/Get_Department_list`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ AuthToken: authToken, parent, fullDoc: "true" }),
        });
        const data = await res.json();
        return data.departments || null;
    };

    const fetchDepartments = async () => {
        setIsLoading(true);
        setListError(null);
        try {
            const [ profilesRes, creatorsRes ] = await Promise.allSettled([ listDepartments(), fetchCreators() ]);
            const creators = creatorsRes.status === "fulfilled" ? creatorsRes.value : null;
            let profiles: DepartmentProfile[] | null = profilesRes.status === "fulfilled" ? profilesRes.value : null;

            if (!profiles && creators) {
                profiles = creators
                    .filter((dep: any) => dep && typeof dep === "object" && dep.name)
                    .map((dep: any) => ({
                        departmentName: dep.name, displayName: dep.name, head: null, hidden: false,
                        createdBy: dep.createdBy || null, createdAt: dep.createdAt || null,
                    }));
            }
            if (!profiles) {
                setListError(`Failed to load ${L.departments.toLowerCase()}`);
                return;
            }

            const creatorByName = new Map((creators || []).filter((d: any) => d?.name).map((d: any) => [ d.name, d.creator ]));
            setDepartments(profiles.map((p) => {
                const name = shownName(p);
                const creator: any = creatorByName.get(p.departmentName);
                return {
                    name,
                    rawName: p.departmentName,
                    code: name.split(" ").map((w: string) => w[ 0 ]).join("").toUpperCase(),
                    head: p.head?.name || p.head?.eid || "",
                    hidden: p.hidden,
                    creatorName: creator?.name || "",
                    creatorEmail: creator?.email || "",
                    profile: p,
                };
            }));
        } catch (err) {
            setListError("Something went wrong. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!depName.trim()) {
            setCreateError(`${L.department} name is required`);
            return;
        }
        setIsCreating(true);
        setCreateError(null);
        try {
            const authToken = Cookies.get("authToken");
            const parent = Cookies.get("ins_code");
            const E_id = Cookies.get("E_id");

            const res = await fetch(`${environment.BASE_URL}/Create_Department`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    authToken,
                    parent,
                    E_id,
                    Name: depName.trim(),
                }),
            });
            const data = await res.json();

            if (data.success) {
                // Refresh list
                setCreateOpen(false);
                setDepName("");
                await fetchDepartments();
            } else {
                setCreateError(data.error || `Failed to create ${L.department.toLowerCase()}`);
            }
        } catch (err) {
            setCreateError("Something went wrong. Please try again.");
        } finally {
            setIsCreating(false);
        }
    };

    const handleOpenCreate = () => {
        setDepName("");
        setCreateError(null);
        setCreateOpen(true);
    };

    return (
        <div className="min-h-screen bg-[oklch(0.97_0.01_90)] p-6 md:p-8">

            {/* HEADER */}
            <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">{L.department} Management</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Create and manage {L.departments.toLowerCase()} and their {L.programmes.toLowerCase()}
                    </p>
                </div>
                {canCreate && (
                    <Button onClick={handleOpenCreate} className="gap-2 shrink-0 shadow-lg shadow-primary/20">
                        <Plus className="h-4 w-4" /> Create {L.department}
                    </Button>
                )}
            </div>

            {/* STATS */}
            <div className="mb-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="flex items-center gap-4 p-5">
                        <div className="bg-primary/10 p-2 rounded-lg">
                            <LayoutGrid className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Total {L.departments}</p>
                            <p className="text-2xl font-bold">{departments.length}</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* TABLES */}
            <Tabs value={view} onValueChange={(v) => setView(v as typeof view)} className="mb-4">
                <TabsList>
                    <TabsTrigger value="departments">{L.departments}</TabsTrigger>
                    <TabsTrigger value="programmes">{L.programmes}</TabsTrigger>
                </TabsList>
            </Tabs>

            {view === "programmes" ? (
                <Card className="overflow-hidden p-0 gap-0">
                    <ProgrammesTable
                        departments={departments}
                        canManage={canManageProgrammes}
                        reloadKey={programmesVersion}
                        onOpenDepartment={setProgrammesDept}
                    />
                </Card>
            ) : (
            <Card>
                <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <CardTitle>{L.departments}</CardTitle>
                        <CardDescription>{filtered.length} {L.department.toLowerCase()}(s)</CardDescription>
                    </div>
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            className="pl-9"
                            placeholder={`Search ${L.department.toLowerCase()}...`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </CardHeader>

                <CardContent className="p-0 relative min-h-[200px]">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-48">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : listError ? (
                        <div className="flex flex-col items-center justify-center h-48 gap-2">
                            <AlertCircle className="h-8 w-8 text-red-400" />
                            <p className="text-sm text-muted-foreground">{listError}</p>
                            <Button variant="outline" size="sm" onClick={fetchDepartments}>
                                Retry
                            </Button>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 gap-2">
                            <Building2 className="h-10 w-10 text-muted-foreground/20" />
                            <p className="text-sm text-muted-foreground">No {L.departments.toLowerCase()} found</p>
                            {canCreate && (
                                <Button variant="outline" size="sm" onClick={handleOpenCreate}>
                                    Create your first {L.department.toLowerCase()}
                                </Button>
                            )}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="pl-6">{L.department}</TableHead>
                                    <TableHead>Head</TableHead>
                                    <TableHead>Created By</TableHead>
                                    <TableHead className="w-12 pr-4"><span className="sr-only">Actions</span></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((dep) => (
                                    <TableRow key={dep.rawName} className="cursor-pointer" onClick={() => setProgrammesDept(dep.profile)}>
                                        <TableCell className="pl-6">
                                            <div className="flex items-center gap-2">
                                                <div className="bg-primary/10 p-1 rounded-md">
                                                    <Building2 className="h-4 w-4 text-primary" />
                                                </div>
                                                <span className="font-medium">{dep.name}</span>
                                                {dep.hidden && (
                                                    <Badge variant="outline" className="gap-1 text-[10px]"><EyeOff className="h-3 w-3" /> Hidden</Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="font-medium">{dep.head || "—"}</span>
                                        </TableCell>
                                        <TableCell>
                                            {dep.creatorName ? (
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{dep.creatorName}</span>
                                                    <span className="text-xs text-muted-foreground">{dep.creatorEmail}</span>
                                                </div>
                                            ) : "—"}
                                        </TableCell>
                                        <TableCell className="w-12 pr-4 text-right">
                                            <RowActions label={dep.name} actions={[
                                                { label: L.programmes, icon: GraduationCap, onSelect: () => setProgrammesDept(dep.profile) },
                                                { label: "Members", icon: Users, onSelect: () => setRosterDept(dep) },
                                                { label: "Edit", icon: Pencil, onSelect: () => setEditDept(dep.profile), hidden: !canCreate },
                                                { label: "Advanced settings", icon: SlidersHorizontal, onSelect: () => setPolicyDept(dep.profile), hidden: !canCreate },
                                            ]} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
            )}

            {/* CREATE DIALOG */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-md border-border/60 p-0 overflow-hidden shadow-2xl">
                    <DialogHeader className="border-b border-border/40 p-6 pb-4 bg-muted/10">
                        <DialogTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-primary" />
                            Create {L.department}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="p-6 space-y-4">
                        {createError && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                                <AlertCircle className="h-4 w-4 shrink-0" />
                                {createError}
                            </div>
                        )}
                        <div className="space-y-1.5">
                            <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-tight">
                                {L.department} Name <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                placeholder="e.g. Computer Science"
                                value={depName}
                                onChange={(e) => setDepName(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                                className="text-sm h-9 border-border/60"
                                autoFocus
                            />
                            <p className="text-[11px] text-muted-foreground">
                                Shown as you type it. A short key for chats and storage is made from it automatically.
                            </p>
                        </div>
                    </div>

                    <DialogFooter className="bg-muted/30 p-4 border-t border-border/40 gap-2">
                        <Button
                            variant="ghost"
                            className="text-xs font-bold uppercase tracking-widest"
                            onClick={() => setCreateOpen(false)}
                            disabled={isCreating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreate}
                            disabled={isCreating || !depName.trim()}
                            className="min-w-[140px] text-xs font-bold uppercase tracking-widest shadow-lg shadow-primary/20"
                        >
                            {isCreating ? (
                                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating...</>
                            ) : (
                                `Create ${L.department}`
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* MEMBERS ROSTER */}
            <DepartmentRoster
                open={rosterDept !== null}
                onClose={() => setRosterDept(null)}
                departmentName={rosterDept?.rawName || null}
                departmentDisplayName={rosterDept?.name || ""}
            />

            {/* EDIT PROFILE */}
            <DepartmentEditDialog
                department={editDept}
                onClose={() => setEditDept(null)}
                onSaved={() => { setEditDept(null); fetchDepartments(); }}
            />

            {/* ADVANCED: ATTENDANCE POLICY */}
            <AdvancedSettings key={policyDept?.departmentName} department={policyDept} onClose={() => setPolicyDept(null)} />

            {/* PROGRAMMES */}
            <ProgrammesPanel
                department={programmesDept}
                canManage={canManageProgrammes}
                onClose={() => { setProgrammesDept(null); setProgrammesVersion(v => v + 1); }}
            />

        </div>
    );
}
